import { getSubdomainsForDomains } from '@/constants/healthDomains';
import { TContentDomain } from '@/types/Content.types';
import { useState } from 'react';
import { TArticleSectionDraft, TBrandedFormErrors, TBrandedFormValues } from './types';

const buildInitialValues = (defaults?: Partial<TBrandedFormValues>): TBrandedFormValues => ({
  title: defaults?.title ?? '',
  description: defaults?.description ?? '',
  domains: defaults?.domains ?? [],
  subdomains: defaults?.subdomains ?? [],
  body: defaults?.body ?? '',
  sections: defaults?.sections ?? [],
  videoSource: defaults?.videoSource ?? 'mux',
  videoFile: null,
  muxStatus: defaults?.muxStatus ?? null,
  muxPlaybackId: defaults?.muxPlaybackId ?? null,
  youtubeUrl: defaults?.youtubeUrl ?? '',
  fullDescription: defaults?.fullDescription ?? '',
});

// Owns the branded-content form model (field values, thumbnail, validation errors)
// so the wizard view can focus on step navigation and rendering.
export const useBrandedContentForm = (defaults?: Partial<TBrandedFormValues>) => {
  const [values, setValues] = useState<TBrandedFormValues>(() => buildInitialValues(defaults));
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<TBrandedFormErrors>({});

  const setField = <K extends keyof TBrandedFormValues>(key: K, value: TBrandedFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const setSections = (sections: TArticleSectionDraft[]) => setField('sections', sections);

  // Changing the selected domains drops any subdomains that no longer belong to the set.
  const setDomains = (domains: TContentDomain[]) =>
    setValues((prev) => {
      const allowed = getSubdomainsForDomains(domains);
      return {
        ...prev,
        domains,
        subdomains: prev.subdomains.filter((subdomain) => allowed.includes(subdomain)),
      };
    });

  const setSubdomains = (subdomains: string[]) => setField('subdomains', subdomains);

  return {
    values,
    setField,
    setSections,
    setDomains,
    setSubdomains,
    thumbnailFile,
    setThumbnailFile,
    errors,
    setErrors,
  };
};
